import { useMutation, useQuery } from 'react-query'

import { queryClient } from 'store/state'

import {
  createMyLeadershipProfileApi,
  createUserLeadershipProfileApi,
  getLeadershipProfileApi,
  listMyLeadershipProfilesApi,
  listUserLeadershipProfilesApi
} from './api'

const KEYS = {
  MINE: 'leadership-profiles/me',
  USER: 'leadership-profiles/user',
  ONE: 'leadership-profiles/one'
}

const TERMINAL_STATUSES = ['complete', 'failed']

export const useMyLeadershipProfiles = () => {
  const { data, isLoading, refetch } = useQuery(
    KEYS.MINE,
    listMyLeadershipProfilesApi
  )
  return { profiles: data || [], isLoading, refetch }
}

export const useUserLeadershipProfiles = userId => {
  const queryKey = [KEYS.USER, userId]
  const { data, isLoading, refetch } = useQuery(
    queryKey,
    () => listUserLeadershipProfilesApi(userId),
    { enabled: Boolean(userId) }
  )
  return { profiles: data || [], isLoading, refetch }
}

export const useLeadershipProfile = (
  profileId,
  { pollIntervalMs = 5000, enabled = true } = {}
) => {
  const queryKey = [KEYS.ONE, profileId]
  const { data, isLoading, error, refetch } = useQuery(
    queryKey,
    () => getLeadershipProfileApi(profileId),
    {
      enabled: enabled && Boolean(profileId),
      refetchInterval: latest => {
        if (!pollIntervalMs) return false
        const status = latest && latest.status
        if (status && TERMINAL_STATUSES.includes(status)) {
          return false
        }
        return pollIntervalMs
      }
    }
  )
  return { profile: data || null, isLoading, error, refetch }
}

export const useCreateLeadershipProfile = ({ userId } = {}) => {
  const invalidateLists = () => {
    queryClient.invalidateQueries(KEYS.MINE)
    if (userId) {
      queryClient.invalidateQueries([KEYS.USER, userId])
    }
  }

  const mutation = useMutation(
    payload => {
      if (userId) {
        return createUserLeadershipProfileApi({
          userId,
          ...payload
        })
      }
      return createMyLeadershipProfileApi(payload)
    },
    { onSuccess: invalidateLists }
  )

  return {
    create: mutation.mutateAsync,
    isCreating: mutation.isLoading,
    createError: mutation.error,
    reset: mutation.reset,
    createdProfile: mutation.data
  }
}
